import { formatDateTime } from '~/utils/date';

import type {
  ProductImagesRead,
  ProductMasterLatestRun,
  ProductMasterRow,
} from '../types/product-master.api.types';

import { pairThumbUrl, pickFullUrl, pickThumbUrl } from './image-urls';

/**
 * A product's picture for a thumb + enlarge viewer: `thumb` is the small rendition to draw (160px
 * thumbnail when recorded), `src` the full image (optimized, else source) and `gallery` the rest.
 */
export interface ProductGallery {
  src: string | null;
  thumb: string | null;
  gallery: string[];
}

/**
 * Flattens a product's per-listing images (P6) into one thumb + gallery: the
 * first PRIMARY image (optimized URL when available, else the source) leads,
 * every other distinct image follows for the enlarged viewer.
 */
export function toProductGallery(images: ProductImagesRead | undefined): ProductGallery {
  return galleryFromPairs((images?.listings ?? []).flatMap((listing) => listing.images));
}

/** The fields of a source → optimized image pair a thumbnail needs. */
interface GalleryPair {
  role: string;
  position: number;
  sourceUrl: string;
  optimized: { url: string; thumbnailUrl?: string | null } | null;
}

/**
 * Thumb + viewer gallery from image pairs: PRIMARY first, then by position; each pair shows its
 * optimized copy (small WebP on GCS) and falls back to the merchant's source only when none exists.
 * `thumb` is the lead pair's 160px thumbnail when recorded (else the same as `src`).
 */
export function galleryFromPairs(pairs: readonly GalleryPair[]): ProductGallery {
  const sorted = [...pairs].sort((a, b) => {
    if (a.role === b.role) return a.position - b.position;
    if (a.role === 'PRIMARY') return -1;
    if (b.role === 'PRIMARY') return 1;
    return 0;
  });
  const urls = sorted
    .map((pair) => pair.optimized?.url ?? pair.sourceUrl)
    .filter((url): url is string => !!url);
  const unique = [...new Set(urls)];
  const lead = sorted[0];
  return {
    src: unique[0] ?? null,
    thumb: lead ? pairThumbUrl(lead) : null,
    gallery: unique.slice(1),
  };
}

/** The picture fields a product row / variant row / detail carries since api-changes §48. */
interface RowPicture {
  primaryImageUrl?: string | null;
  thumbnailUrl?: string | null;
}

/**
 * A row's own picture (`thumbnailUrl` → `primaryImageUrl`), used instead of a per-row images read
 * when the backend sends the fields. `primaryImageUrl` is already the optimized copy, else the source.
 */
export function rowPicture(row: RowPicture): ProductGallery {
  const primary = row.primaryImageUrl ?? null;
  return {
    src: pickFullUrl({ optimizedUrl: primary }),
    thumb: pickThumbUrl({ thumbnailUrl: row.thumbnailUrl ?? null, optimizedUrl: primary }),
    gallery: [],
  };
}

/**
 * A product's header / preview picture: its images read once loaded (whole gallery), else — while
 * that read is pending, or when it has none — the detail's own §48 `thumbnailUrl` / `primaryImageUrl`.
 */
export function productPicture(
  product: RowPicture,
  images: ProductImagesRead | undefined,
): ProductGallery {
  const fromImages = toProductGallery(images);
  return fromImages.src ? fromImages : rowPicture(product);
}

/**
 * Props for `ProductCell` / `ProductCardItem`: the small rendition drawn in the box (`imageUrl`),
 * the full image the enlarged viewer opens first (`fullImageUrl`), then the rest of the gallery.
 */
export function thumbProps(picture: ProductGallery | undefined): {
  imageUrl: string | null;
  fullImageUrl: string | null;
  gallery: string[];
} {
  if (!picture) return { imageUrl: null, fullImageUrl: null, gallery: [] };
  return {
    imageUrl: picture.thumb ?? picture.src,
    fullImageUrl: picture.src,
    gallery: picture.gallery,
  };
}

/** First 8 characters of a uuid — enough to tell rows apart without a wall of hex. */
export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : '—';
}

/** One "Group by: Latest batch" bucket — the current PAGE's rows for one batch (or none). */
export interface ProductMasterGroup {
  /** The batch's id, or `'none'` for the "No batch yet" bucket. */
  key: string;
  run: ProductMasterLatestRun | null;
  rows: ProductMasterRow[];
}

/**
 * S3 — groups the current page's rows by `latestRun` (newest batch first; "No batch yet" last).
 * Client-side and page-scoped on purpose: the brief asks to group what's already loaded, not to
 * add a server-side grouped read. A row whose `latestRun` is `undefined` (older backend, the field
 * was never sent) is treated the same as `null` — there is nothing to group it by either way.
 */
export function groupProductsByLatestRun(rows: ProductMasterRow[]): ProductMasterGroup[] {
  const groups: ProductMasterGroup[] = [];
  const byKey = new Map<string, ProductMasterGroup>();
  for (const row of rows) {
    const run = row.latestRun ?? null;
    const key = run?.id ?? 'none';
    const existing = byKey.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      const group: ProductMasterGroup = { key, run, rows: [row] };
      byKey.set(key, group);
      groups.push(group);
    }
  }
  // Newest batch first (by its own startedAt; a batch not started yet after every started one);
  // the "No batch yet" bucket always sorts last.
  return groups.sort((a, b) => {
    if (!a.run) return 1;
    if (!b.run) return -1;
    return (b.run.startedAt ?? '').localeCompare(a.run.startedAt ?? '');
  });
}

/** A nullable ISO timestamp as a localized date-time, or an em dash. */
export function formatMaybeDateTime(iso: string | null | undefined): string {
  return iso ? formatDateTime(iso) : '—';
}
